import { fetchWithTimeout } from "../utils/fetchWithTimeout";

import { getLastKnownAuthUser } from "./authService";
import type {
  HistoryFilter,
  WithdrawalHistoryDetailData,
  WithdrawalHistoryItem,
  WithdrawalHistoryPageData,
} from "../types/withdrawalHistory";

export type HistorySource = "server" | "cache";

interface CachedValue<T> {
  key: string;
  userId: number;
  savedAt: string;
  value: T;
}

const DB_NAME = "materialentnahme-history";
const DB_VERSION = 1;
const STORE = "history";
const CACHE_SCHEMA = "v0.132-history-v4";

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(STORE)) {
        request.result.createObjectStore(STORE, { keyPath: "key" });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("Verlaufsspeicher konnte nicht geöffnet werden."));
  });
}

function userId(): number | null {
  return getLastKnownAuthUser()?.id ?? null;
}

async function cachePut<T>(key: string, value: T): Promise<void> {
  const id = userId();
  if (id === null || !("indexedDB" in globalThis)) return;
  const db = await openDb();
  try {
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, "readwrite");
      tx.objectStore(STORE).put({ key: `${id}:${key}`, userId: id, savedAt: new Date().toISOString(), value } satisfies CachedValue<T>);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } finally { db.close(); }
}

async function cacheGet<T>(key: string): Promise<T | null> {
  const id = userId();
  if (id === null || !("indexedDB" in globalThis)) return null;
  const db = await openDb();
  try {
    return await new Promise((resolve, reject) => {
      const request = db.transaction(STORE, "readonly").objectStore(STORE).get(`${id}:${key}`);
      request.onsuccess = () => {
        const stored = request.result as CachedValue<T> | undefined;
        resolve(stored?.userId === id ? stored.value : null);
      };
      request.onerror = () => reject(request.error);
    });
  } finally { db.close(); }
}

function historyListKey(params: { query: string; filter: HistoryFilter; dateFrom: string; dateTo: string; offset: number; limit?: number }): string {
  const limit = params.limit ?? 50;
  const search = new URLSearchParams({ q: params.query, filter: params.filter, offset: String(params.offset), limit: String(limit) });
  if (params.dateFrom) search.set("date_from", params.dateFrom);
  if (params.dateTo) search.set("date_to", params.dateTo);
  return `${CACHE_SCHEMA}:list:${search.toString()}`;
}

export async function getCachedHistory(params: {
  query: string; filter: HistoryFilter; dateFrom: string; dateTo: string; offset: number; limit?: number;
}): Promise<WithdrawalHistoryPageData | null> {
  return await cacheGet<WithdrawalHistoryPageData>(historyListKey(params)).catch(() => null);
}

export async function fetchHistory(params: {
  query: string;
  filter: HistoryFilter;
  dateFrom: string;
  dateTo: string;
  offset: number;
  limit?: number;
}): Promise<{ data: WithdrawalHistoryPageData; source: HistorySource }> {
  const limit = params.limit ?? 50;
  const search = new URLSearchParams({
    q: params.query,
    filter: params.filter,
    offset: String(params.offset),
    limit: String(limit),
  });
  if (params.dateFrom) search.set("date_from", params.dateFrom);
  if (params.dateTo) search.set("date_to", params.dateTo);

  const key = historyListKey(params);
  try {
    const response = await fetchWithTimeout(`/api/withdrawals/history?${search.toString()}`, {
      credentials: "include",
      cache: "no-store",
      headers: { Accept: "application/json" },
    }, 10_000);
    if (!response.ok) throw new Error(`Verlauf konnte nicht geladen werden (${response.status}).`);
    const data = await response.json() as WithdrawalHistoryPageData;
    await cachePut(key, data).catch(() => undefined);
    return { data, source: "server" };
  } catch (error) {
    const cached = await cacheGet<WithdrawalHistoryPageData>(key).catch(() => null);
    if (cached) return { data: cached, source: "cache" };
    if (error instanceof DOMException && error.name === "AbortError") throw new Error("Der Verlauf konnte innerhalb von 10 Sekunden nicht geladen werden. Prüfe die Verbindung und versuche es erneut.", { cause: error });
    throw error;
  }
}

/**
 * Vollständige Verlaufsobjekte nur für die sichtbare History-Seite.
 * Der globale Recovery-/Statuspfad nutzt bewusst den separaten schlanken
 * /sync-recovery/incomplete-Snapshot und importiert diesen schweren Pfad nicht.
 */
export async function fetchIncompleteHistoryItems(): Promise<WithdrawalHistoryItem[]> {
  const items: WithdrawalHistoryItem[] = [];
  let offset = 0;
  const limit = 100;

  while (true) {
    const result = await fetchHistory({
      query: "",
      filter: "pending",
      dateFrom: "",
      dateTo: "",
      offset,
      limit,
    });
    const page = result.data.items.filter((item) =>
      item.status === "completed" && (item.sync_status !== "synced" || !item.pdf_present)
    );
    items.push(...page);
    offset += result.data.items.length;
    if (result.data.items.length < limit || offset >= result.data.total) break;
  }

  return items;
}

export async function fetchHistoryDetail(id: number): Promise<{ data: WithdrawalHistoryDetailData; source: HistorySource }> {
  const key = `${CACHE_SCHEMA}:detail:${id}`;
  try {
    const response = await fetchWithTimeout(`/api/withdrawals/history/${id}`, {
      credentials: "include",
      cache: "no-store",
      headers: { Accept: "application/json" },
    }, 2500);
    if (!response.ok) throw new Error(`Entnahme konnte nicht geladen werden (${response.status}).`);
    const data = await response.json() as WithdrawalHistoryDetailData;
    await cachePut(key, data).catch(() => undefined);
    return { data, source: "server" };
  } catch (error) {
    const cached = await cacheGet<WithdrawalHistoryDetailData>(key).catch(() => null);
    if (cached) return { data: cached, source: "cache" };
    throw error;
  }
}

export async function fetchAdminHistoryDetail(id: number): Promise<{ data: WithdrawalHistoryDetailData; source: HistorySource }> {
  const response = await fetchWithTimeout(`/api/withdrawals/admin/history/${id}`, {
    credentials: "include",
    cache: "no-store",
    headers: { Accept: "application/json" },
  }, 2500);
  if (!response.ok) throw new Error(`Entnahme konnte nicht geladen werden (${response.status}).`);
  return { data: await response.json() as WithdrawalHistoryDetailData, source: "server" };
}

export async function deleteWithdrawal(id: number): Promise<void> {
  const response = await fetch(`/api/withdrawals/${id}`, {
    method: "DELETE",
    credentials: "include",
  });
  if (!response.ok) throw new Error(`Entnahme konnte nicht als gelöscht markiert werden (${response.status}).`);
}

export async function pruneWithdrawalHistoryCache(options: { maxEntries?: number; maxAgeDays?: number } = {}): Promise<number> {
  if (!("indexedDB" in globalThis)) return 0;
  const maxEntries = Math.max(20, options.maxEntries ?? 120);
  const maxAgeMs = Math.max(1, options.maxAgeDays ?? 30) * 24 * 60 * 60 * 1000;
  const cutoff = Date.now() - maxAgeMs;
  const db = await openDb();
  try {
    const rows = await new Promise<Array<CachedValue<unknown>>>((resolve, reject) => {
      const request = db.transaction(STORE, "readonly").objectStore(STORE).getAll();
      request.onsuccess = () => resolve(request.result as Array<CachedValue<unknown>>);
      request.onerror = () => reject(request.error);
    });
    const sorted = [...rows].sort((a, b) => Date.parse(b.savedAt) - Date.parse(a.savedAt));
    const removeKeys = sorted
      .filter((row, index) => index >= maxEntries || !Number.isFinite(Date.parse(row.savedAt)) || Date.parse(row.savedAt) < cutoff)
      .map((row) => row.key);
    if (!removeKeys.length) return 0;
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, "readwrite");
      const store = tx.objectStore(STORE);
      for (const key of removeKeys) store.delete(key);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error);
    });
    return removeKeys.length;
  } finally { db.close(); }
}
